export type Theme = "light" | "dark"

export const THEME_STORAGE_KEY = "hoa-theme"
export const THEME_ATTRIBUTE = "data-theme"

// Runs before first paint; without it a dark-mode reader gets a white
// flash on navigation. Keep dependency-free and try/catch-wrapped —
// Safari private mode throws on localStorage — and small; it blocks parsing.
export const NO_FLASH_SCRIPT = `(function(){try{var k=${JSON.stringify(
  THEME_STORAGE_KEY,
)};var s=window.localStorage.getItem(k);var t=(s==="dark"||s==="light")?s:((window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches)?"dark":"light");document.documentElement.setAttribute(${JSON.stringify(
  THEME_ATTRIBUTE,
)},t);}catch(e){}})();`

/** The theme currently painted, read from the DOM rather than from storage. */
export function currentTheme(): Theme {
  if (typeof document === "undefined") return "light"
  return document.documentElement.getAttribute(THEME_ATTRIBUTE) === "dark"
    ? "dark"
    : "light"
}

/** Paints a theme without recording a choice. */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return
  document.documentElement.setAttribute(THEME_ATTRIBUTE, theme)
}

// Records an explicit choice — from here on the stored preference
// outranks OS-preference changes on every page.
export function toggleTheme(): void {
  const next: Theme = currentTheme() === "dark" ? "light" : "dark"
  applyTheme(next)
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, next)
  } catch (e) {
    // Private mode / storage disabled — the theme still applies for this page.
  }
}

// Tracks OS preference only until the reader makes an explicit choice —
// then this stops having an opinion.
export function watchSystemTheme(): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {}
  const query = window.matchMedia("(prefers-color-scheme: dark)")
  const onChange = (event: MediaQueryListEvent) => {
    let stored: string | null = null
    try {
      stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    } catch (e) {
      stored = null
    }
    if (stored === "dark" || stored === "light") return
    applyTheme(event.matches ? "dark" : "light")
  }
  query.addEventListener("change", onChange)
  return () => query.removeEventListener("change", onChange)
}
